import { CategoryInterface, useCategoriesQuery, useRouteQuery } from '@magentoTypes'
import { useQueryClient } from '@tanstack/react-query'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { DEFAULT_LOCALE } from '~/config/constants'
import { createMagentoClient } from '~/graphql/magentoClient'
import { PageType, useSEO } from '~/hooks/useSEO'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import CioBrowseResultPage from './CioBrowseResultPage'
import { VariationsStrategy } from './lib/constants'

type CioCategoryPageProps = {
  slug: string
  filterName: string
  filterValue: string
  showVariationsStrategy?: VariationsStrategy
}

const CioCategoryPage = ({
  slug,
  filterName,
  filterValue,
  showVariationsStrategy,
}: CioCategoryPageProps) => {
  const router = useRouter()
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const queryClient = useQueryClient()
  const magentoClient = createMagentoClient({ queryClient, locale: finalLocale })

  const { data: routeData } = useRouteQuery(
    magentoClient,
    { slug: slug?.replace(/^-/, '') as string },
    { enabled: !!slug },
  )

  const { data: categoriesData } = useCategoriesQuery(
    magentoClient,
    { uid: (routeData?.route as unknown as CategoryInterface)?.uid },
    { enabled: !!routeData?.route },
  )

  const { canonical, languageAlternates } = useSEO({
    identifier: (routeData?.route as unknown as CategoryInterface)?.id,
    type: PageType.MAGENTO_CATEGORY_PAGE,
  })

  return (
    <>
      {canonical && languageAlternates && (
        <NextSeo canonical={canonical} languageAlternates={languageAlternates} />
      )}
      <CioBrowseResultPage
        slug={slug}
        filterName={filterName}
        filterValue={filterValue}
        headline={categoriesData?.categoryList?.[0]?.name}
        showVariationsStrategy={showVariationsStrategy}
      />
    </>
  )
}

export default CioCategoryPage
